/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : App.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author Tahir Shaik - tahir@au79consulting.com
 * @date 11-06-2024
 * @description This file is having the major route to redirect to a particular page
 */
//Importing the Components from the modules/libraries
import "./App.css";
import { Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import Dashboard from "./main-layout/Dashboard";
import "react-toastify/dist/ReactToastify.css";
import {
  Authentication,
  Home,
  // Vta,
  // LabCar,
  PageNotFound,
  ProfileConfig,
  UserManagement,
  // LegalRequirements,
  Templates,
  AdminSettings,
  ViewAllNotifications,
} from "./pages";
import ProtectedRoute from "./ProtectedRoute";
import Docs from "./pages/documetation/Docs";
import Notifications from "./main-layout/TopBar/Notifications";
import RouteProject from "./pages/project-config/RouteProject";
import DocumentLibrary from "./pages/document_library/DocumentLibrary";
import RepositoryMain from "./pages/test-cases/RepositoryMain";
import CampaignMain from "./pages/test-cases/CampaignMain";
import ViewTestCampaign from "./pages/test-cases/components/ViewTestCampaign";
import ViewTestCampaignLogs from "./pages/test-cases/components/ViewTestCampaignLogs";
import RunTestCampaign from "./pages/test-cases/components/RunTestCampaign";
import Settings from "./pages/settings/Settings";
import RuleCenter from "./pages/rule-engine/rule_center";
import VlcIndex from "./components/customComponentsV2/vlc/VlcIndex";

import Integration from "./pages/integrations/Integration";
import LegalTables from "./pages/legal-requirements/Tables/LegalTables";
import LegalRequirementsV2 from "./pages/legal-requirements/LegalRequirements/LegalRequirementsV2";
import RCHomePage from "./pages/regulatory-compliance/RCHomePage";
import LinkHome from "./pages/regulatory-compliance/Link/LinkHome";
import RaiseTicket from "./pages/regulatory-compliance/create/RaiseTicket";
import IntersectionIndex from "./pages/regulatory-compliance/intersections/IntersectionIndex";
import TicketsIndex from "./pages/regulatory-compliance/tickets/TicketsIndex";
import TraceTrack from "./pages/regulatory-compliance/intersections/TraceTrack";
import DocumentLibraryIndex from "./pages/document_library/V2/DocumentLibraryIndex";
import StimulationFileIndex from "./pages/document_library/V2/StimulationFileIndex";
import NotificationIndex from "./main-layout/TopBar/V2/NotificationIndex";
import MindMapPage from "./pages/labcar/MindMapPage";
import ReportDesigner from "./pages/admin-settings/DynamicFormV2/ReportDesigner";
import LinkHomePage from "./components/customComponentsV2/vlc/pages/LinkHomePage";
import RuleCenterPage from "./pages/legal-requirements/RuleCenter/RuleCenterPage";
import RuleSetWorkflowPage from "./pages/legal-requirements/RuleCenter/RuleSetWorkflowPage";
//Main App functional component
function App() {
  return (
    <div className="App">
      <Routes>
        <Route path="/authenticate" element={<Authentication />} />
        <Route exact path="/" element={<Navigate to="/dashboard" />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        >
          {" "}
          <Route
            path="/dashboard"
            element={<Navigate to="/dashboard/admin-settings/profile" />}
          />
          <Route path="/dashboard/home" element={<Home />} />
          <Route path="/dashboard/docs" element={<Docs />} />
          <Route path="/dashboard/project-config" element={<RouteProject />} />
          {/* <Route path="/dashboard/type-approval" element={<Vta />} /> */}
          <Route path="/dashboard/v2/type-approval" element={<RCHomePage />}>
            <Route path="link" element={<LinkHome />} />
            <Route path="link/create" element={<RaiseTicket />} />
            <Route path="intersection" element={<IntersectionIndex />} />
            <Route path="tickets" element={<TicketsIndex />} />
            <Route path="traceTrack" element={<TraceTrack />} />
          </Route>
          {/* <Route path="/dashboard/labcar" element={<LabCar />} /> */}
          <Route path="/dashboard/v2/mindmap" element={<MindMapPage />} />
          <Route path="/dashboard/v2/link" element={<LinkHomePage />} />
          <Route path="/dashboard/v2/*" element={<VlcIndex />} />
          <Route
            path="/dashboard/user-management"
            element={<UserManagement />}
          />
          <Route
            path="/dashboard/test-cases/repository"
            element={
              <ProtectedRoute>
                <RepositoryMain />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/test-cases/campaign"
            element={
              <ProtectedRoute>
                <CampaignMain />
              </ProtectedRoute>
            }
          >
            <Route path="view/:id" element={<ViewTestCampaign />} />
            <Route path="log/:id" element={<ViewTestCampaignLogs />} />
            <Route path="run/:id" element={<RunTestCampaign />} />
          </Route>
          <Route
            path="/dashboard/legal-requirements"
            element={<LegalRequirementsV2 />}
          />
          <Route path="/dashboard/legal-tables" element={<LegalTables />} />
          <Route path="/dashboard/rule-center" element={<RuleCenterPage />} />
          <Route
            path="/dashboard/rule-center/create"
            element={<RuleSetWorkflowPage />}
          />
          <Route
            path="/dashboard/rule-center/:ruleSetId/edit"
            element={<RuleSetWorkflowPage />}
          />
          {/* <Route
            path="/dashboard/legal-requirements-old"
            element={<LegalRequirements />}
          /> */}
          <Route path="/dashboard/templates" element={<Templates />} />
          {/* <Route path="/dashboard/admin-settings" element={<AdminSettings />} /> */}
          <Route path="/dashboard/admin-settings/" element={<Settings />}>
            {/* <Route index element={<ProfileConfig />} /> */}
            <Route path="profile" element={<ProfileConfig />} />
            <Route path="ddh_repository" element={<AdminSettings />} />
            <Route path="docs" element={<Docs />} />
            <Route path="account-access" element={<UserManagement />} />
          </Route>
          <Route path="/dashboard/dynamicform" element={<ReportDesigner />} />
          <Route path="/dashboard/integration" element={<Integration />} />
          <Route path="/dashboard/notifications" element={<Notifications />} />
          <Route
            path="/dashboard/doc_library/reports"
            element={<DocumentLibraryIndex />}
          />
          <Route
            path="/dashboard/doc_library/stimulation"
            element={<StimulationFileIndex />}
          />
          <Route
            path="/dashboard/doc_library/old"
            element={<DocumentLibrary />}
          />
          <Route path="/dashboard/doc_library" element={<DocumentLibrary />} />
          <Route
            path="/dashboard/view-all-notifications"
            element={<ViewAllNotifications />}
          />
          <Route
            path="/dashboard/notification"
            element={<NotificationIndex />}
          />
          <Route
            path="/dashboard/v2/notification"
            element={<ViewAllNotifications />}
          />
        </Route>
        <Route path="/dashboard/*" element={<PageNotFound />} />
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={false}
        icon={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
}

export default App;
